import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  LinearProgress,
  Stack,
  Typography,
} from '@mui/material';
import type { Job, JobStatus } from '../../core/types/data-models';

interface JobDetailDialogProps {
  open: boolean;
  job: Job | null;
  projectName?: string;
  onClose: () => void;
  /** Present only when `job` is PENDING/PROCESSING; renders the "Cancel job" action. */
  onCancel?: () => void;
}

const STATUS_COLORS: Record<JobStatus, 'default' | 'info' | 'success' | 'error'> = {
  PENDING: 'default',
  PROCESSING: 'info',
  COMPLETED: 'success',
  FAILED: 'error',
  CANCELLED: 'default',
};

function Field({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="body2">{value}</Typography>
    </Box>
  );
}

/** Pretty-prints JSON-encoded job fields; falls back to the raw string if it isn't valid JSON. */
function prettyJson(raw: string): string {
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
}

function JsonBlock({ label, content }: { label: string; content: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Box
        component="pre"
        sx={{
          mt: 0.5,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          fontSize: 12,
          fontFamily: 'monospace',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
          maxHeight: 280,
          overflowY: 'auto',
        }}
      >
        {prettyJson(content)}
      </Box>
    </Box>
  );
}

/** Read-only view of a single background job — matches LlmRequestDetailDialog's shape, plus a cancel action for still-active jobs. */
export function JobDetailDialog({ open, job, projectName, onClose, onCancel }: JobDetailDialogProps) {
  if (!job) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" spacing={1}>
          <span>Job #{job.id}</span>
          <Chip label={job.status} size="small" color={STATUS_COLORS[job.status]} variant="outlined" />
        </Stack>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          {job.error && <Alert severity="error">{job.error}</Alert>}

          <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
            <Field label="Created" value={new Date(job.createdAt).toLocaleString()} />
            <Field label="Project" value={projectName ?? (job.projectId ? `#${job.projectId}` : '—')} />
            <Field label="User" value={job.userId ? `#${job.userId}` : '—'} />
            <Field label="Job type" value={job.jobType} />
            <Field label="Priority" value={job.priority} />
          </Stack>

          <Box>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Progress
            </Typography>
            <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mt: 0.5 }}>
              <Box sx={{ flex: 1 }}>
                <LinearProgress variant="determinate" value={job.progressPercent} sx={{ height: 8, borderRadius: 1 }} />
              </Box>
              <Typography variant="body2" sx={{ minWidth: 40, textAlign: 'right' }}>{job.progressPercent}%</Typography>
            </Stack>
            {job.progressMessage && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{job.progressMessage}</Typography>
            )}
          </Box>

          <Divider />

          {job.data && <JsonBlock label="Input data" content={job.data} />}
          {job.result && <JsonBlock label="Result" content={job.result} />}
          {job.progressAdditionalData && <JsonBlock label="Progress data" content={job.progressAdditionalData} />}
        </Stack>
      </DialogContent>
      <DialogActions>
        {onCancel && (
          <Button color="error" onClick={onCancel}>Cancel job</Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
